/* ==========================================================================
   Homellence – Bild-Band: feststehender Hintergrund

   Beim Scrollen bleibt das Bild stehen, der Abschnitt schiebt sich darüber.

   Bewusst **nicht** über `background-attachment: fixed` (arbeitet auf iOS
   nicht) und **nicht** über `position: fixed` (das war in Runde 10 die
   Ursache dafür, dass vom Bild kaum etwas übrig blieb: der Bildrahmen stand
   über die ganze Bildschirmhöhe und das Band zeigte davon nur einen
   Ausschnitt).

   Stattdessen: Der Bildrahmen liegt im Band und ist um die Einstellung
   „Spielraum" (`overscan`) höher als das Band. Genau um diesen Überstand –
   und keinen Pixel weiter – wird er im Scroll-Verlauf verschoben. Oben und
   unten kann deshalb keine Lücke entstehen.

   Gerechnet wird in `requestAnimationFrame`, beobachtet über einen
   IntersectionObserver: Wer nicht im Bild ist, rechnet nicht mit.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* --------------------------------------------------------------------
     Das Bild ist da, bevor das Band zu sehen ist

     Auf der Waesche-Seite war beim schnellen Herunterscrollen einen Moment
     nur die marineblaue Flaeche da. Grund: `loading="lazy"`. Der Rahmen ist
     durch `overscan` noch hoeher als das ohnehin hohe Band, und der Browser
     faengt erst an zu laden, wenn das Band fast im Bild ist.

     - Das **erste** Band einer Seite laedt sofort (`eager`, hohe Prioritaet).
     - Alle weiteren bleiben `lazy`, werden aber ueber einen eigenen
       Beobachter mit grosszuegiger `rootMargin` frueh angestossen: 200 % der
       Bildschirmhoehe vorher, also lange bevor sie sichtbar werden.

     Am Platz aendert das nichts - `width`/`height` stehen am Bild, der
     Rahmen liegt absolut im Band. Kein Layout-Sprung.
     -------------------------------------------------------------------- */
  var PRELOAD_MARGIN = '200% 0px';

  function bandImages(el) {
    return Array.prototype.slice.call(el.querySelectorAll('.hl-band__media img'));
  }

  function loadNow(el) {
    bandImages(el).forEach(function (img) {
      if (img.getAttribute('loading') === 'eager') return;
      img.setAttribute('loading', 'eager');
      if ('fetchPriority' in img) img.fetchPriority = 'high';
      else img.setAttribute('fetchpriority', 'high');
    });
  }

  /* --------------------------------------------------------------------
     B1 · Das Bild wird erst nach dem ersten Scrollen gezeichnet

     `translate3d` hebt den Bildrahmen auf eine eigene Compositing-Ebene.
     Wird diese Ebene angelegt, **bevor** das Bild dekodiert ist, bleibt sie
     leer - und ohne Anlass rastert der Browser sie nicht neu. Sichtbar wurde
     das als „sechs Sekunden nur die marineblaue Flaeche", obwohl das Bild
     laengst geladen war.

     Zwei Gegenmittel, beide noetig:
     - Die Position wird einmal beim Aufbau gerechnet, unabhaengig von
       Scroll und Beobachter (`kick()`).
     - Sobald ein Bild fertig ist, wird die Ebene ausdruecklich fuer
       ungueltig erklaert und neu gerechnet.

     `will-change` steht nur noch, solange das Band im Bild ist, und wird
     nicht mehr bei jedem Beobachter-Ereignis hin- und hergeschaltet:
     Jeder Wechsel wirft die Ebene weg und legt sie neu an - genau der
     Zustand, aus dem der Fehler entsteht.
     -------------------------------------------------------------------- */
  function repaint(media) {
    if (!media) return;
    // Ein erzwungener Stilwechsel auf derselben Ebene. `translateZ(0)` bleibt
    // dieselbe Darstellung, markiert die Ebene aber als ungueltig.
    var before = media.style.transform;
    media.style.transform = before ? before + ' translateZ(0)' : 'translateZ(0)';
    // Layout lesen erzwingt das Neurechnen, bevor der alte Wert zurueckkommt.
    void media.offsetHeight;
    media.style.transform = before;
  }

  class HlImageBand extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('.hl-band__media');
      if (!this.media) return;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);

      this.setupLoading();

      if (reduced.addEventListener) reduced.addEventListener('change', this.onMotionChange);
      else if (reduced.addListener) reduced.addListener(this.onMotionChange);

      this.start();
      this.kick();
    }

    /* Einmal rechnen und zeichnen, ohne auf Scrollen oder den Beobachter zu
       warten - und noch einmal, sobald jedes Bild wirklich da ist. */
    kick() {
      var self = this;

      var draw = function () {
        self.update();
        repaint(self.media);
      };

      requestAnimationFrame(function () {
        requestAnimationFrame(draw);
      });

      bandImages(this).forEach(function (img) {
        if (img.complete && img.naturalWidth) {
          requestAnimationFrame(draw);
          return;
        }
        img.addEventListener('load', function () {
          requestAnimationFrame(draw);
        }, { once: true });
      });

      // Sicherheitsnetz fuer Browser, die `load` am zwischengespeicherten
      // Bild verschlucken.
      window.addEventListener('load', draw, { once: true });
    }

    /* Erstes Band der Seite sofort, alle weiteren frueh vorgeladen. */
    setupLoading() {
      var first = document.querySelector('hl-image-band');
      if (first === this) {
        loadNow(this);
        return;
      }

      if (!('IntersectionObserver' in window)) {
        loadNow(this);
        return;
      }

      var self = this;
      this.preloader = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            loadNow(self);
            if (self.preloader) {
              self.preloader.disconnect();
              self.preloader = null;
            }
          });
        },
        { rootMargin: PRELOAD_MARGIN }
      );
      this.preloader.observe(this);
    }

    disconnectedCallback() {
      if (this.preloader) {
        this.preloader.disconnect();
        this.preloader = null;
      }
      this.stop();
      if (reduced.removeEventListener) reduced.removeEventListener('change', this.onMotionChange);
      else if (reduced.removeListener) reduced.removeListener(this.onMotionChange);
    }

    /* Der Effekt läuft nur, wenn die Einstellung ihn erlaubt, ein Spielraum
       da ist und der Besucher keine reduzierte Bewegung eingestellt hat. */
    /* Y5 (Runde 29) - Kennt der Browser scrollgesteuerte Animationen, macht
       er die Bewegung selbst, und dieses Skript haelt sich ganz heraus.

       Gemessen am 16.09.: Die Nachfuehrung im rAF hinkte dem Scrollstand um
       genau einen Scrollschritt hinterher - 40 px nach 40 px Scrollen. Das
       ist der Nachlauf, den Kristin als Zittern sieht, und er laesst sich im
       Hauptfaden nicht wegoptimieren: Gescrollt wird auf dem Compositor.

       Der alte Weg bleibt als Rueckfall stehen. Dort zittert es weiter -
       aber ein annaehernd stehendes Bild ist besser als ein mitlaufendes. */
    browserKannEs() {
      return typeof CSS !== 'undefined'
        && typeof CSS.supports === 'function'
        && CSS.supports('animation-timeline', 'view()');
    }

    wanted() {
      if (reduced.matches) return false;
      if (this.getAttribute('data-parallax') !== 'true') return false;
      if (this.browserKannEs()) return false;
      return this.overscan() > 0;
    }

    overscan() {
      var value = parseFloat(this.getAttribute('data-overscan') || '0');
      if (!isFinite(value) || value < 0) return 0;
      return Math.min(40, value) / 100;
    }

    start() {
      if (this.running) return;
      if (!this.wanted()) {
        this.reset();
        return;
      }
      this.running = true;
      this.visible = false;
      this.frame = null;

      var self = this;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              self.visible = entry.isIntersecting;
              // Nur einschalten, nie wieder ausschalten: Jeder Wechsel wirft
              // die Compositing-Ebene weg und legt sie leer neu an.
              if (self.visible && self.media.style.willChange !== 'transform') {
                self.media.style.willChange = 'transform';
              }
              if (self.visible) self.onScroll();
            });
          },
          { rootMargin: '15% 0px' }
        );
        this.observer.observe(this);
      } else {
        this.visible = true;
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.update();
    }

    stop() {
      this.running = false;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    reset() {
      if (!this.media) return;
      this.media.style.transform = '';
      this.media.style.willChange = 'auto';
    }

    onMotionChange() {
      this.stop();
      this.reset();
      this.start();
    }

    onResize() {
      if (this.running) this.onScroll();
    }

    onScroll() {
      if (!this.visible || this.frame) return;
      var self = this;
      this.frame = requestAnimationFrame(function () {
        self.frame = null;
        self.update();
      });
    }

    /* W3 (Runde 27) - Stillstand statt Verlangsamung.

       Der Rahmen ist so hoch wie das Fenster und sitzt im Band bei `top: 0`.
       Seine Position im Fenster ist damit gleich der Bandposition. Schieben
       wir ihn um genau diese Position zurueck, steht er im Fenster still -
       das Band wandert daran vorbei wie ein Loch vor einem Bild.

           Verschiebung = -rect.top

       Mehr braucht es nicht. Die frueheren `progress`- und
       `overscan`-Rechnungen sind entfallen; sie ergaben eine Bewegung von
       rund 91 % der Seitengeschwindigkeit statt 0 %.

       Unter 750 px bleibt der Rahmen deckungsgleich mit dem Band (CSS), und
       hier steht dann eine Verschiebung von 0 - sonst wuerde eine Luecke
       entstehen. Die Grenze wird einmal ueber `matchMedia` gelesen, nicht
       ueber die Fensterbreite bei jedem Bild. */
    fensterModus() {
      return window.matchMedia('(min-width: 750px)').matches;
    }

    update() {
      if (!this.media) return;

      /* Y5 (Runde 29) - Kennt der Browser `view()`, schreibt dieses Skript
         **gar keine** Verschiebung mehr. Ohne diese Sperre lief `kick()`
         beim Laden einmal durch und hinterliess einen festen Wert: Gemessen
         am 16.09. stand `translate3d(0, -2453px, 0)` am Bildrahmen, waehrend
         die Huelle darueber sauber bei 0 lag - das Bild war damit um die
         halbe Seite verschoben und wanderte beim Scrollen mit. */
      if (this.browserKannEs()) {
        if (this.media.style.transform) this.reset();
        return;
      }

      var rect = this.getBoundingClientRect();
      if (!rect.height) return;

      if (!this.fensterModus()) {
        this.media.style.transform = 'translate3d(0, 0, 0)';
        return;
      }

      var shift = -rect.top;
      this.media.style.transform = 'translate3d(0, ' + shift.toFixed(2) + 'px, 0)';
    }
  }

  if (!customElements.get('hl-image-band')) customElements.define('hl-image-band', HlImageBand);
})();
